// #1711 物販のキャンペーン価格 — 期間と名称を付けて、終わったら自動で戻す。
//
// **サーバ (services/pay/api/services/menu_campaign.py) と同じ判定**を画面側にも置く。
// 正はサーバで、API から返る menu は既に期限切れが通常価格へ戻されている。
// ここで再度かけるのは、開いたまま期限をまたいだ端末 (客の商品一覧・レジ・スタッフ
// 注文) で、古い価格を見せたりカートに入れたりしないため。再評価のタイミングは
// hooks/useEffectiveMenu.ts が持つ (1 分ごと + 画面復帰時)。

// = services/pay/api/services/menu_campaign.py MAX_CAMPAIGN_NAME_LEN
export const CAMPAIGN_NAME_MAX_LEN = 60

/** 期限判定に要る最小の形。MenuItem 以外 (レジ・スタッフ注文の絞った型) にも効かせるため。 */
export type CampaignPriced = {
  price: number
  compareAtPrice?: number | null
  compareAtPriceKind?: 'manufacturer' | 'store'
  campaignName?: string | null
  campaignNameEn?: string | null
  campaignEndAt?: string | null
}

/** 二重価格が成立しているか (比較対照 > 販売価格)。客側の表示・期限判定・管理側バッジで共通。 */
export function hasActiveCompareAt(item: Pick<CampaignPriced, 'price' | 'compareAtPrice'>): boolean {
  return (item.compareAtPrice ?? 0) > item.price
}

/** 終了日時を Date にする。無い・壊れているなら null。 */
export function campaignEndDate(item: Pick<CampaignPriced, 'campaignEndAt'>): Date | null {
  if (!item.campaignEndAt) return null
  const d = new Date(item.campaignEndAt)
  return Number.isNaN(d.getTime()) ? null : d
}

/** 期限切れか。終了日時が無い・二重価格が成立していないなら false (戻す先が無い)。 */
export function isCampaignExpired(item: CampaignPriced, now: Date = new Date()): boolean {
  const end = campaignEndDate(item)
  if (!end || !hasActiveCompareAt(item)) return false
  return end.getTime() <= now.getTime()
}

/** 期限切れなら通常価格 (= compareAtPrice) へ戻した item を返す。期限内はそのまま (同一参照)。 */
export function effectiveMenuItem<T extends CampaignPriced>(item: T, now: Date = new Date()): T {
  if (!isCampaignExpired(item, now)) return item
  return {
    ...item,
    price: item.compareAtPrice as number,
    compareAtPrice: null,
    compareAtPriceKind: undefined,
    campaignName: null,
    campaignNameEn: null,
    campaignEndAt: null,
  }
}

/** 客に見せるキャンペーン名。ja 以外は英語名 (無ければ日本語名) にフォールバック。 */
export function campaignDisplayName(item: Pick<CampaignPriced, 'campaignName' | 'campaignNameEn'>, locale: string): string | null {
  const ja = (item.campaignName || '').trim()
  const en = (item.campaignNameEn || '').trim()
  if (locale === 'ja') return ja || en || null
  return en || ja || null
}
